/**
 * RideCard — used in search results and "my rides" listings. Always shows
 * proposed cost alongside reference cost (transparency promise).
 */
import type { RideDto } from '@waylo/shared';
import { jsLocale, useT } from '../lib/i18n';
import { Badge } from './Badge';
import { Card } from './Card';
import { ReliabilityBadge } from './ReliabilityBadge';

function formatDateTime(iso: string, loc: string): { date: string; time: string } {
  const d = new Date(iso);
  const date = d.toLocaleDateString(loc, { weekday: 'short', day: 'numeric', month: 'short' });
  const time = d.toLocaleTimeString(loc, { hour: '2-digit', minute: '2-digit', hour12: false });
  return { date, time };
}

function formatCost(amount: number, currency: string): string {
  return `${Math.round(amount).toLocaleString()} ${currency}`;
}

interface Props {
  ride: RideDto;
  onOpen?: () => void;
  hideDriver?: boolean;
}

export function RideCard({ ride, onOpen, hideDriver }: Props): JSX.Element {
  const { t, locale } = useT();
  const { date, time } = formatDateTime(ride.departureAt, jsLocale(locale));
  const ratio = ride.proposedCost / Math.max(1, ride.referenceCost);
  const costTone: 'success' | 'neutral' | 'warning' | 'danger' =
    ratio <= 1.05 ? 'success' : ratio <= 1.2 ? 'neutral' : ratio <= 1.5 ? 'warning' : 'danger';
  const statusBadge: { tone: 'neutral' | 'success' | 'warning' | 'danger'; label: string } | null =
    ride.status === 'full'
      ? { tone: 'warning', label: t('status.full') }
      : ride.status === 'under_review'
        ? { tone: 'warning', label: t('status.under_review') }
        : ride.status === 'cancelled_by_driver' || ride.status === 'rejected'
          ? { tone: 'danger', label: t('status.cancelled') }
          : ride.status === 'completed' || ride.status === 'auto_completed'
            ? { tone: 'neutral', label: t('status.completed') }
            : null;
  const nameKey: 'nameSr' | 'nameEn' = locale === 'en' ? 'nameEn' : 'nameSr';
  // Sub-path search: the searched A→B segment + its fare (if this is a partial match).
  const seg = ride.matchedSegment;
  const allStops = [ride.route.origin, ...ride.viaCities, ride.route.destination];
  const stopName = (id: string): string => allStops.find((c) => c.id === id)?.[nameKey] ?? '';
  const shownCost = seg ? seg.price : ride.proposedCost;

  return (
    <Card onClick={onOpen} className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm text-tg-hint">{date}</p>
          <p className="font-semibold text-tg-text">
            {ride.route.origin[nameKey]} → {ride.route.destination[nameKey]}
          </p>
          {ride.viaCities.length > 0 && (
            <p className="truncate text-xs text-tg-hint">
              {t('ride.via.short', {
                stops: ride.viaCities.map((c) => c[nameKey]).join(' · '),
              })}
            </p>
          )}
          {seg && (
            <p className="truncate text-xs font-medium text-brand-green">
              {t('segment.yourTrip', { from: stopName(seg.fromCityId), to: stopName(seg.toCityId) })}
            </p>
          )}
          <p className="text-sm text-tg-hint">{time}</p>
        </div>
        <div className="text-right">
          <p className="tnum font-display text-base font-bold text-tg-text">
            {formatCost(shownCost, ride.currency)}
          </p>
          <p className="tnum text-xs text-tg-hint">
            ref {formatCost(ride.referenceCost, ride.currency)}
          </p>
          {costTone !== 'neutral' && (
            <Badge tone={costTone} className="mt-1">
              {costTone === 'success'
                ? t('ride.cost.fair')
                : costTone === 'warning'
                  ? t('ride.cost.warn')
                  : t('ride.cost.high')}
            </Badge>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3">
        {!hideDriver && (
          <div className="flex items-center gap-2 truncate">
            {ride.driver.avatarUrl ? (
              <img
                src={ride.driver.avatarUrl}
                alt=""
                width={28}
                height={28}
                className="rounded-full"
              />
            ) : (
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-navy text-xs text-brand-cream">
                {ride.driver.displayName.slice(0, 1)}
              </div>
            )}
            <span className="truncate text-sm text-tg-text">{ride.driver.displayName}</span>
            {ride.driver.ratingAvg !== null && (
              <span className="tnum text-xs text-tg-hint">★ {ride.driver.ratingAvg.toFixed(1)}</span>
            )}
            {ride.driver.isDriver && (
              <ReliabilityBadge
                score={ride.driver.reliability}
                ridesCompleted={ride.driver.ridesCompleted}
              />
            )}
          </div>
        )}
        <div className="flex items-center gap-2">
          {ride.preferences.womenOnly && <Badge tone="brand">{t('ride.pref.womenOnly')}</Badge>}
          {statusBadge && <Badge tone={statusBadge.tone}>{statusBadge.label}</Badge>}
          <Badge tone="neutral">
            {ride.seatsRemaining}/{ride.seatsTotal} {t('common.seat_other')}
          </Badge>
        </div>
      </div>
    </Card>
  );
}
